import * as React from "react";
import { DatasheetGridSettingsMarginType } from "@common/dto/datasheet/grid-settings-dto";
import { DatasheetFieldComponentGetDto } from "@common/dto/datasheet/field-component/get-dto";
import { DatasheetElement, DatasheetElementId, DatasheetElementType } from "@modules/datasheets/types/datasheet-element";
import { DatasheetLocalGetDto } from "../../types/datasheet";
import { isFieldTypeTabNavigable, NON_TAB_FIELD_TYPES } from "@modules/datasheets/utils/tab-navigation";

type Props = {
  editTabOrderActive: boolean;
  datasheet: DatasheetLocalGetDto;
  datasheetElements: Record<DatasheetElementId, DatasheetElement>;
  onDatasheetElementsChange: (datasheetElements: Record<DatasheetElementId, DatasheetElement>) => void;
};

export const useGridSettingsController = (props: Props) => {
  const { datasheet, datasheetElements, onDatasheetElementsChange, editTabOrderActive } = props;
  const [currentLatestTabOrder, setCurrentLatestTabOrder] = React.useState(0); 
  
  React.useEffect(() => {
    if (editTabOrderActive) {
      setCurrentLatestTabOrder(0);
    }
  }, [editTabOrderActive]);

  const gridSettingsMargin = React.useMemo(() => {
    const dimensions: Record<DatasheetGridSettingsMarginType, { width: number; height: number }> = {
      [DatasheetGridSettingsMarginType.fhq]: {
        width: 1100,
        height: 450,
      },
      [DatasheetGridSettingsMarginType.tablet]: {
        width: 300,
        height: 650,
      },
      [DatasheetGridSettingsMarginType.custom]: {
        width: datasheet.gridSettings.marginWidth,
        height: datasheet.gridSettings.marginHeight
      },
    };

    if (datasheet.gridSettings.marginType) {
      return {
        show: datasheet.gridSettings.showMargins,
        ...dimensions[datasheet.gridSettings.marginType],
      };
    }

    return {
      show: false,
      width: 0,
      height: 0,
    };
  }, [datasheet.gridSettings]);

  const pageBreaks = {
    show: datasheet.gridSettings.showPageBreaks,
    height: 1015,
    width: 582,
  };

  const handleDatasheetElementsTabOrderChange = React.useCallback((datasheetElement: DatasheetElement<DatasheetFieldComponentGetDto>) => {
    if (datasheetElement.original.field?.system) {
      return;
    }

    const df = datasheetElement.original.field;
    const dft = (df as any)?.fieldtype?.typeid ?? (df as any)?.type ?? (df as any)?.rawFieldType;
    if (NON_TAB_FIELD_TYPES.has(dft)) {
      return;
    }

    const fieldCount = Object.values(datasheetElements)
      .filter((x) => {
        if (x.type !== DatasheetElementType.field) return false;
        const el = x as DatasheetElement<DatasheetFieldComponentGetDto>;
        const ef = el.original.field;
        const eft = (ef as any)?.fieldtype?.typeid ?? (ef as any)?.type ?? (ef as any)?.rawFieldType;
        return isFieldTypeTabNavigable(eft, Boolean(ef?.system));
      })
      .length;

    const newTabOrder = (currentLatestTabOrder + 1) > fieldCount
      ? 1
      : currentLatestTabOrder + 1;
    const previousTabOrder = currentLatestTabOrder;
    setCurrentLatestTabOrder(newTabOrder);

    const newElements = Object.keys(datasheetElements).reduce<Record<DatasheetElementId, DatasheetElement>>((acc, id) => {
      const element = datasheetElements[id];

      if (!element) {
        return acc;
      }

      if (element.type === DatasheetElementType.text) {
        acc[id] = element;
        return acc;
      }

      const elementAsFieldComponent = element as DatasheetElement<DatasheetFieldComponentGetDto>;

      if (elementAsFieldComponent.original.field?.system) {
        acc[id] = element;
        return acc;
      }

      const nef = elementAsFieldComponent.original.field;
      const neft = (nef as any)?.fieldtype?.typeid ?? (nef as any)?.type ?? (nef as any)?.rawFieldType;
      if (NON_TAB_FIELD_TYPES.has(neft)) {
        acc[id] = element;
        return acc;
      }

      if (elementAsFieldComponent.id === datasheetElement.id) {
        const matchedEl: DatasheetElement<DatasheetFieldComponentGetDto> = {
          ...datasheetElement,
          original: {
            ...datasheetElement.original,
            tabOrder: newTabOrder,
          },
        };
        acc[id] = matchedEl;
        return acc;
      }

      let computedTabOrder = elementAsFieldComponent.original.tabOrder;

      if (newTabOrder === 1 && elementAsFieldComponent.original.tabOrder < datasheetElement.original.tabOrder) {
        computedTabOrder = elementAsFieldComponent.original.tabOrder + 1;
      }

      if (newTabOrder > 1) {
        if (
          elementAsFieldComponent.original.tabOrder < datasheetElement.original.tabOrder &&
          elementAsFieldComponent.original.tabOrder >= newTabOrder
        ) {
          computedTabOrder = elementAsFieldComponent.original.tabOrder + 1;
        } else if (
          elementAsFieldComponent.original.tabOrder <= newTabOrder &&
          elementAsFieldComponent.original.tabOrder > previousTabOrder
        ) {
          computedTabOrder = elementAsFieldComponent.original.tabOrder - 1;
        }
      }

      const shiftedEl: DatasheetElement<DatasheetFieldComponentGetDto> = {
        ...elementAsFieldComponent,
        original: {
          ...elementAsFieldComponent.original,
          tabOrder: computedTabOrder,
        },
      };
      acc[id] = shiftedEl;

      return acc;
    }, {});

    onDatasheetElementsChange(newElements);
  }, [onDatasheetElementsChange, datasheetElements, currentLatestTabOrder]);

  const handleResetTabOrder = React.useCallback(() => {
    let tabOrderCounter = 0;
    let systemTabOrderCounter = 0;
    let nonTabOrderCounter = 0;
    const orderByYElements = Object.values(datasheetElements)
      .sort((a, b) => a.coordinates.y - b.coordinates.y)
      .map((x) => {
        if (x.type === DatasheetElementType.text) {
          return x;
        }

        const fieldEl = x as DatasheetElement<DatasheetFieldComponentGetDto>;
        if (fieldEl.original.field?.system) {
          return {
            ...x,
            original: {
              ...x.original,
              tabOrder: ++systemTabOrderCounter,
            },
          };
        }

        const rf = fieldEl.original.field;
        const rft = (rf as any)?.fieldtype?.typeid ?? (rf as any)?.type ?? (rf as any)?.rawFieldType;
        if (NON_TAB_FIELD_TYPES.has(rft)) {
          return {
            ...x,
            original: {
              ...x.original,
              tabOrder: ++nonTabOrderCounter,
            },
          };
        }

        return {
          ...x,
          original: {
            ...x.original,
            tabOrder: ++tabOrderCounter,
          },
        };
      })
      .reduce<Record<DatasheetElementId, DatasheetElement>>((acc, x) => {
        acc[x.id] = x;
        return acc;
      }, {});

    onDatasheetElementsChange({ ...datasheetElements, ...orderByYElements });
    setCurrentLatestTabOrder(0);
  }, [datasheetElements, onDatasheetElementsChange]);

  return {
    handleResetTabOrder,
    handleDatasheetElementsTabOrderChange,
    gridSettingsMargin,
    pageBreaks,
  };
};